import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, List, Card, Skeleton, Empty, Row, Typography, Button } from 'antd';
import { ArrowLeftOutlined, SearchOutlined, CaretRightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { PasscodesDeviceDto } from 'services/passcodes/passcodes.service';
import { LockPickerFragmentProps } from 'fragments/passcodes/fragments/lock-picker/interfaces';
import { useLockPickerController } from 'fragments/passcodes/fragments/lock-picker/lock-picker.controller';
import { SiteGroup } from 'fragments/passcodes/fragments/lock-picker/lock-grouping';
import { VirtualizedLockList } from 'fragments/passcodes/fragments/lock-picker/virtualized-lock-list';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import ErrorComponent from 'components/error-component/error-component.component';
import 'fragments/passcodes/fragments/lock-picker/lock-picker.scss';

const { Text } = Typography;

export const LockPickerFragment: React.FC<LockPickerFragmentProps> = (props) => {
    const navigate = useNavigate();
    const { translate } = useTranslator();
    // default para el modo standalone de dev: volver atrás. El paso REAL de la selección
    // al abridor lo hace el flujo de generación via prop onSelect (plan aparte).
    const defaultOnSelect = () => navigate(-1);
    const onSelect = props.onSelect ?? defaultOnSelect;
    const useController = props.useController ?? useLockPickerController;
    const controller = useController(onSelect);

    const onBack = () => {
        if (controller.view === 'site') {
            controller.exitSite();
            return;
        }
        props.onClose ? props.onClose() : navigate(-1);
    };

    // Pluralización explícita por código (no dependemos del sufijo _plural de i18next).
    const lockCountLabel = (count: number): string =>
        translate({ key: count === 1 ? 'lock.picker-lock-count' : 'lock.picker-lock-count-plural', extra: { count } });
    const resultCountLabel = (count: number): string =>
        translate({
            key: count === 1 ? 'lock.picker-result-count' : 'lock.picker-result-count-plural',
            extra: { count },
        });

    // Cards operables por teclado (Enter/Espacio) + expuestas al árbol de accesibilidad,
    // igual que el botón back nativo. antd Card es un <div>, así que lo hacemos rol button.
    const cardProps = (onActivate: () => void) => ({
        role: 'button' as const,
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate();
            }
        },
    });

    const renderEmptySearch = () => (
        <Empty
            className={'lock-picker-empty'}
            description={
                <>
                    <div className={'lock-picker-lockname'} style={{ whiteSpace: 'normal' }}>
                        {translate({ key: 'lock.picker-empty-search', extra: { query: controller.query } })}
                    </div>
                    <Text className={'lock-picker-sitename'}>
                        {translate({ key: 'lock.picker-empty-search-hint' })}
                    </Text>
                </>
            }
        />
    );

    // Card de una cerradura (sin List.Item) — la consume la lista virtual.
    const renderLockCard = (lock: PasscodesDeviceDto, withSite: boolean) => (
        <Card className={'lock-picker-card'} {...cardProps(() => controller.selectDevice(lock))}>
            <div className={'lock-picker-lockname'}>{lock.lock_name}</div>
            {withSite && <div className={'lock-picker-sitename'}>{lock.site_name}</div>}
        </Card>
    );

    const renderRecentItem = (lock: PasscodesDeviceDto) => (
        <List.Item key={lock.id}>
            <Card className={'lock-picker-card'} {...cardProps(() => controller.selectDevice(lock))}>
                <Row align={'middle'} wrap={false}>
                    <ClockCircleOutlined aria-hidden style={{ color: '#6b6478', marginRight: 10 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={'lock-picker-lockname'}>{lock.lock_name}</div>
                        <div className={'lock-picker-sitename'}>{lock.site_name}</div>
                    </div>
                </Row>
            </Card>
        </List.Item>
    );

    const renderSiteItem = (site: SiteGroup) => (
        <List.Item key={site.siteName}>
            <Card className={'lock-picker-card'} {...cardProps(() => controller.enterSite(site.siteName))}>
                <Row align={'middle'} justify={'space-between'} wrap={false}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={'lock-picker-lockname'}>{site.siteName}</div>
                        <Text className={'lock-picker-sitecount'}>{lockCountLabel(site.count)}</Text>
                    </div>
                    <CaretRightOutlined aria-hidden style={{ color: 'rgba(0,0,0,0.25)' }} />
                </Row>
            </Card>
        </List.Item>
    );

    const { view, browseModel, siteModel } = controller;
    const ready = !controller.isLoading && !controller.hasError;

    return (
        <div className={'lock-picker'}>
            <div className={'lock-picker-header'}>
                <button className={'lock-picker-back'} aria-label={translate({ key: 'general.back' })} onClick={onBack}>
                    <ArrowLeftOutlined aria-hidden />
                </button>
                <Input
                    className={'lock-picker-search'}
                    placeholder={
                        view === 'site'
                            ? translate({ key: 'lock.picker-search-site', extra: { site: controller.activeSiteName } })
                            : translate({ key: 'lock.picker-search-all' })
                    }
                    prefix={<SearchOutlined aria-hidden style={{ color: '#bfbfbf' }} />}
                    allowClear
                    value={controller.query}
                    onChange={(e) => controller.setQuery(e.target.value)}
                />
            </div>

            {controller.hasError && (
                <div className={'lock-picker-body lock-picker-body--scroll'}>
                    <div className={'lock-picker-error'}>
                        <ErrorComponent />
                        <Button type={'primary'} onClick={controller.retry} className={'lock-picker-retry'}>
                            {translate({ key: 'lock.picker-retry' })}
                        </Button>
                    </div>
                </div>
            )}

            {!controller.hasError && controller.isLoading && (
                <div className={'lock-picker-body lock-picker-body--scroll'} style={{ paddingTop: '1rem' }}>
                    <Skeleton active title paragraph={{ rows: 6 }} />
                </div>
            )}

            {/* BROWSE — recientes + sitios (o vacío sin cerraduras) */}
            {ready &&
                view === 'browse' &&
                browseModel.mode === 'browse' &&
                (browseModel.sites.length === 0 && browseModel.recents.length === 0 ? (
                    <div className={'lock-picker-body lock-picker-body--scroll'}>
                        <Empty
                            className={'lock-picker-empty'}
                            description={translate({ key: 'lock.picker-no-locks' })}
                        />
                    </div>
                ) : (
                    <div className={'lock-picker-body lock-picker-body--scroll'}>
                        {browseModel.recents.length > 0 && (
                            <>
                                <div className={'lock-picker-section-title'}>
                                    {translate({ key: 'lock.picker-recents' })}
                                </div>
                                <List
                                    className={'lock-picker-list'}
                                    dataSource={browseModel.recents}
                                    renderItem={renderRecentItem}
                                />
                            </>
                        )}
                        <div className={'lock-picker-section-title'}>{translate({ key: 'lock.picker-sites' })}</div>
                        <List
                            className={'lock-picker-list'}
                            dataSource={browseModel.sites}
                            renderItem={renderSiteItem}
                        />
                    </div>
                ))}

            {/* BROWSE — resultados de búsqueda global (planos) */}
            {ready &&
                view === 'browse' &&
                browseModel.mode === 'results' &&
                (browseModel.results.length > 0 ? (
                    <>
                        <div className={'lock-picker-section-title'}>
                            {resultCountLabel(browseModel.results.length)}
                        </div>
                        <div className={'lock-picker-body'}>
                            <VirtualizedLockList
                                data={browseModel.results}
                                renderRow={(lock) => renderLockCard(lock, true)}
                            />
                        </div>
                    </>
                ) : (
                    <div className={'lock-picker-body lock-picker-body--scroll'}>{renderEmptySearch()}</div>
                ))}

            {/* DETALLE DE SITIO — breadcrumb + lista scoped (o vacío) */}
            {ready && view === 'site' && (
                <>
                    <div className={'lock-picker-breadcrumb'}>
                        {controller.activeSiteName}{' '}
                        <Text className={'lock-picker-count'}>· {lockCountLabel(controller.activeSiteCount)}</Text>
                    </div>
                    {siteModel.length > 0 ? (
                        <div className={'lock-picker-body'}>
                            <VirtualizedLockList data={siteModel} renderRow={(lock) => renderLockCard(lock, false)} />
                        </div>
                    ) : (
                        <div className={'lock-picker-body lock-picker-body--scroll'}>{renderEmptySearch()}</div>
                    )}
                </>
            )}
        </div>
    );
};
